import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useAbility } from '@casl/react';
import { useTranslate } from '@bynder/localization';

import { IconCampaign, IconCaretDown } from '@bynder/icons';
import { Flex } from '@bynder/design-system';
import { isView } from '../../../helpers/windowLocation';
import { useOutsideClick } from '../../../helpers/useOutsideClick';
import { initModalLinks, getWorkflowData, initCTAMediaUploadButton } from '../../../legacy/legacy';
import { AbilityContext } from '../../../abilities/Can';
import { iconSizeLarge } from '../../../helpers/iconSizes';

const workflow = getWorkflowData();

interface DropdownItem {
	id?: string;
	label: string;
	url?: string;
	href?: string;
	className?: string;
	dataTargetSelector?: string;
	dataImportURL?: string;
	dataAction?: string;
	dataInit?: string;
	onClick?: React.MouseEventHandler<HTMLAnchorElement>;
}

export function JobPageCTA() {
	const refDropdown = useRef<HTMLUListElement>(null);
	const ability = useAbility(AbilityContext);
	const { translate } = useTranslate();

	const [isDropdownOpen, setIsDropdownOpen] = useState(false);
	const [jobViewItems, setJobViewItems] = useState<DropdownItem[]>([]);

	useEffect(() => {
		if (jobViewItems.length) {
			initModalLinks();
			initCTAMediaUploadButton();
		}
	}, [jobViewItems]);

	useOutsideClick(refDropdown, () => {
		setIsDropdownOpen(false);
	});

	const buildJobViewItems = useCallback(() => {
		if (!isView('job/view')) {
			return [];
		}

		const items: DropdownItem[] = [];

		if (ability.can('select', 'workflow_add_asset')) {
			items.push({
				label: translate('workflow.AddAsset'),
				href: '#',
				className: 'focus-button',
				dataTargetSelector: 'click',
			});
		}

		if (
			ability.cannot('select', 'workflow_add_asset') &&
			ability.can('select', 'workflow_add_asset_template')
		) {
			items.push({
				id: 'cta-wf-media-upload',
				label: translate('workflow.AddAsset'),
				href: '#',
				className: '',
				dataImportURL: workflow.uploadURL,
			});
		}

		if (ability.can('select', 'workflow_add_task')) {
			items.push({
				label: translate('workflow.TaskAdd'),
				href: '#',
				className: 'focus-button',
				dataTargetSelector: '#new-task input.task-name-input',
				dataAction: 'focus',
				onClick: (e: React.MouseEvent) => {
					e.preventDefault();

					const taskInput = document.querySelector<HTMLInputElement>(
						'.wf-task-edit-value input#tsk-name-new-task',
					);
					taskInput?.click();
					taskInput?.focus();
				},
			});
		}

		if (ability.can('select', 'workflow_add_stage')) {
			items.push({
				label: translate('workflow.StageAdd'),
				href: `/workflow/stage/add/${workflow.jobId}/`,
				className: 'modal-link',
				dataInit: 'Job.edit',
			});
		}

		return items;
	}, [ability, translate]);

	useEffect(() => {
		const jobItems = buildJobViewItems();
		setJobViewItems(jobItems);
	}, [ability.rules, buildJobViewItems]);

	const toggleDropdown = () => {
		setIsDropdownOpen((prevState) => !prevState);
	};

	const firstJVItem: DropdownItem = jobViewItems[0];

	return jobViewItems.length > 1 ? (
		<ul className="cta-main-container" id="uploadContainer" ref={refDropdown}>
			<li>
				<a
					href="#"
					id={firstJVItem.id}
					className={`cta-main cta-media upload focus-button ${
						jobViewItems.length === 1 ? 'no-dropdown' : ''
					}`}
					data-target-selector={firstJVItem.dataTargetSelector}
					data-importurl={firstJVItem.dataImportURL}
					data-action={firstJVItem.dataAction}
					data-init={firstJVItem.dataInit}
				>
					<Flex gap="2" alignItems="center">
						<IconCampaign size={iconSizeLarge} />
						{firstJVItem.label}
					</Flex>
				</a>
			</li>

			{/* CONTEXTUAL MENU */}
			<li className={isDropdownOpen ? 'show' : ''}>
				<a href="#" onClick={toggleDropdown} className="cta-main cta-dropdown-trigger">
					<IconCaretDown size={iconSizeLarge} />
				</a>
				<ul className="cta-dropdown">
					{jobViewItems.slice(1).map((item: DropdownItem) => (
						<li key={item.label}>
							<a
								href={item.href}
								className={item.className}
								data-target-selector={item.dataTargetSelector}
								data-importurl={item.dataImportURL}
								data-action={item.dataAction}
								data-init={item.dataInit}
								onClick={item.onClick}
							>
								{item.label}
							</a>
						</li>
					))}
				</ul>
			</li>
		</ul>
	) : null;
}
